/**
 * Mock NFL player roster for local Daily Reveal prop-based testing only.
 *
 * Uses clearly fictional names (not real NFL athletes) so the shared package
 * cannot leak real-player IP onto fabricated odds.
 *
 * Runtime gates:
 * - Backend: mock / historical_replay providers are hard-blocked in production
 *   (`NODE_ENV=production`); sportsbook→mock fallback is opt-in via
 *   `NFL_PROP_MARKET_FALLBACK_TO_MOCK=true` and never in production.
 * - Client: `getMockNflPlayerPhotoUrl` / `/mock-players/` are DEV-only; Vite
 *   strips `public/mock-players/` from production builds.
 */

export type MockNflDailyRevealPlayer = {
  playerExternalId: string;
  playerName: string;
  position: string;
  teamExternalId: string;
  teamAbbreviation: string;
  opponentAbbreviation: string;
  jerseyNumber?: string;
  /** Prop stat types this player can appear on when syncing mock markets. */
  propStatTypes: string[];
  /** Per-stat threshold overrides (sportsbook-style .5 lines). */
  thresholds?: Partial<Record<string, number>>;
  /** Served from daily-reveal `public/mock-players/` when present. */
  photoUrl?: string;
};

/** Featured mock players with local headshot assets. */
export const MOCK_NFL_FEATURED_PLAYERS: MockNflDailyRevealPlayer[] = [
  {
    playerExternalId: 'mock-qb-1',
    playerName: 'Alex Rivers',
    position: 'QB',
    teamExternalId: 'nfl-bal',
    teamAbbreviation: 'BAL',
    opponentAbbreviation: 'PIT',
    jerseyNumber: '8',
    propStatTypes: ['passing_yards', 'passing_touchdowns', 'anytime_touchdown'],
    thresholds: { passing_yards: 224.5, passing_touchdowns: 1.5 },
    photoUrl: '/mock-players/qb-player.png',
  },
  {
    playerExternalId: 'mock-rb-1',
    playerName: 'Jordan Pike',
    position: 'RB',
    teamExternalId: 'nfl-phi',
    teamAbbreviation: 'PHI',
    opponentAbbreviation: 'DAL',
    jerseyNumber: '26',
    propStatTypes: ['rushing_yards', 'all_purpose_yards', 'anytime_touchdown'],
    thresholds: { rushing_yards: 77.5, all_purpose_yards: 77.5 },
    photoUrl: '/mock-players/rb-player.png',
  },
  {
    playerExternalId: 'mock-dl-1',
    playerName: 'Marcus Vale',
    position: 'DE',
    teamExternalId: 'nfl-cle',
    teamAbbreviation: 'CLE',
    opponentAbbreviation: 'BAL',
    jerseyNumber: '95',
    propStatTypes: ['sacks'],
    thresholds: { sacks: 0.5 },
    photoUrl: '/mock-players/de-player.png',
  },
];

/** Extra mock players so each day can still build 3-card packs (no custom photos). */
export const MOCK_NFL_SUPPORT_PLAYERS: MockNflDailyRevealPlayer[] = [
  {
    playerExternalId: 'mock-qb-2',
    playerName: 'Casey Holt',
    position: 'QB',
    teamExternalId: 'nfl-buf',
    teamAbbreviation: 'BUF',
    opponentAbbreviation: 'MIA',
    jerseyNumber: '17',
    propStatTypes: ['passing_yards', 'passing_touchdowns', 'anytime_touchdown'],
  },
  {
    playerExternalId: 'mock-qb-3',
    playerName: 'Riley Quinn',
    position: 'QB',
    teamExternalId: 'nfl-cin',
    teamAbbreviation: 'CIN',
    opponentAbbreviation: 'CLE',
    jerseyNumber: '9',
    propStatTypes: ['passing_yards', 'passing_touchdowns', 'anytime_touchdown'],
  },
  {
    playerExternalId: 'mock-rb-2',
    playerName: 'Devon Marsh',
    position: 'RB',
    teamExternalId: 'nfl-bal',
    teamAbbreviation: 'BAL',
    opponentAbbreviation: 'PIT',
    jerseyNumber: '22',
    propStatTypes: ['rushing_yards', 'all_purpose_yards', 'anytime_touchdown'],
  },
  {
    playerExternalId: 'mock-rb-3',
    playerName: 'Sam Ortega',
    position: 'RB',
    teamExternalId: 'nfl-det',
    teamAbbreviation: 'DET',
    opponentAbbreviation: 'GB',
    jerseyNumber: '0',
    propStatTypes: ['rushing_yards', 'all_purpose_yards', 'anytime_touchdown'],
  },
  {
    playerExternalId: 'mock-de-2',
    playerName: 'Blake Navarro',
    position: 'DE',
    teamExternalId: 'nfl-dal',
    teamAbbreviation: 'DAL',
    opponentAbbreviation: 'PHI',
    jerseyNumber: '11',
    propStatTypes: ['sacks'],
  },
  {
    playerExternalId: 'mock-de-3',
    playerName: 'Chris Harlan',
    position: 'DE',
    teamExternalId: 'nfl-pit',
    teamAbbreviation: 'PIT',
    opponentAbbreviation: 'BAL',
    jerseyNumber: '90',
    propStatTypes: ['sacks'],
  },
  {
    playerExternalId: 'mock-wr-1',
    playerName: 'Jamie Cole',
    position: 'WR',
    teamExternalId: 'nfl-mia',
    teamAbbreviation: 'MIA',
    opponentAbbreviation: 'BUF',
    jerseyNumber: '10',
    propStatTypes: ['receiving_yards', 'anytime_touchdown'],
  },
  {
    playerExternalId: 'mock-wr-2',
    playerName: 'Taylor Brooks',
    position: 'WR',
    teamExternalId: 'nfl-det',
    teamAbbreviation: 'DET',
    opponentAbbreviation: 'GB',
    jerseyNumber: '14',
    propStatTypes: ['receiving_yards', 'anytime_touchdown'],
  },
  {
    playerExternalId: 'mock-te-1',
    playerName: 'Avery Knox',
    position: 'TE',
    teamExternalId: 'nfl-kc',
    teamAbbreviation: 'KC',
    opponentAbbreviation: 'LV',
    jerseyNumber: '87',
    propStatTypes: ['receiving_yards', 'anytime_touchdown'],
  },
];

export const MOCK_NFL_DAILY_REVEAL_PLAYERS: MockNflDailyRevealPlayer[] = [
  ...MOCK_NFL_FEATURED_PLAYERS,
  ...MOCK_NFL_SUPPORT_PLAYERS,
];

const PHOTO_BY_PLAYER_ID = Object.fromEntries(
  MOCK_NFL_DAILY_REVEAL_PLAYERS.filter(p => p.photoUrl).map(p => [p.playerExternalId, p.photoUrl!])
);

const PLAYER_BY_ID = Object.fromEntries(
  MOCK_NFL_DAILY_REVEAL_PLAYERS.map(p => [p.playerExternalId, p])
);

/** Mock-only headshot path for a player external id, if configured. */
export function getMockNflPlayerPhotoUrl(playerExternalId: string | null | undefined): string | null {
  if (!playerExternalId) return null;
  return PHOTO_BY_PLAYER_ID[playerExternalId] ?? null;
}

export function getMockNflDailyRevealPlayer(
  playerExternalId: string | null | undefined
): MockNflDailyRevealPlayer | null {
  if (!playerExternalId) return null;
  return PLAYER_BY_ID[playerExternalId] ?? null;
}

export function resolveMockNflPlayerThreshold(
  player: MockNflDailyRevealPlayer,
  propStatType: string,
  fallback: number
): number {
  return player.thresholds?.[propStatType] ?? fallback;
}
